import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Eye, MousePointer, TrendingUp, Globe, Users, Mail, 
  Edit2, Trash2, Plus, X, Save, ToggleLeft, ToggleRight, Clock,
  Gift, Percent, DollarSign, MapPin, FileText, Bell, FlaskConical, ShoppingCart, MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketingDashboard = ({ headers }) => {
  const [activeTab, setActiveTab] = useState('banners');
  const [banners, setBanners] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberSegments, setSubscriberSegments] = useState(null);
  const [popupSettings, setPopupSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showABStats, setShowABStats] = useState(null);
  const [abStats, setAbStats] = useState(null);
  
  // Edit states
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [bannerForm, setBannerForm] = useState({});
  const [couponForm, setCouponForm] = useState({});
  const [showCreateBanner, setShowCreateBanner] = useState(false);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [newBannerForm, setNewBannerForm] = useState({
    name: '',
    title: '',
    text: '',
    cta_text: 'Learn More',
    cta_link: '/',
    location: 'homepage',
    background_color: '#003456',
    is_active: true
  });
  const [newCouponForm, setNewCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    description: '',
    minimum_order: '',
    max_uses: '',
    valid_until: '',
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bannersRes, couponsRes, visitorsRes, subsRes, segmentsRes, popupRes] = await Promise.all([
        axios.get(`${API_URL}/api/marketing/banners/analytics`, { headers }),
        axios.get(`${API_URL}/api/marketing/coupons/analytics`, { headers }),
        axios.get(`${API_URL}/api/marketing/analytics/visitors?days=30`, { headers }),
        axios.get(`${API_URL}/api/marketing/subscribers`, { headers }),
        axios.get(`${API_URL}/api/marketing/subscribers/segments`, { headers }),
        axios.get(`${API_URL}/api/marketing/popup-settings`, { headers })
      ]);
      
      setBanners(bannersRes.data || []);
      setCoupons(couponsRes.data || []);
      setVisitorStats(visitorsRes.data);
      setSubscribers(subsRes.data || []);
      setSubscriberSegments(segmentsRes.data);
      setPopupSettings(popupRes.data);
    } catch (error) {
      console.error('Failed to fetch marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch A/B Stats
  const fetchBannerABStats = async (bannerId) => {
    try {
      const res = await axios.get(`${API_URL}/api/marketing/banners/${bannerId}/ab/stats`, { headers });
      setAbStats(res.data);
      setShowABStats({ type: 'banner', id: bannerId });
    } catch (error) {
      toast.error('Failed to fetch A/B stats');
    }
  };

  const fetchCouponABStats = async (couponId) => {
    try {
      const res = await axios.get(`${API_URL}/api/marketing/coupons/${couponId}/ab/stats`, { headers });
      setAbStats(res.data);
      setShowABStats({ type: 'coupon', id: couponId });
    } catch (error) {
      toast.error('Failed to fetch A/B stats');
    }
  };

  // Banner handlers
  const handleEditBanner = (banner) => {
    setEditingBanner(banner.id);
    setBannerForm({
      name: banner.name,
      title: banner.title,
      text: banner.text,
      cta_text: banner.cta_text,
      cta_link: banner.cta_link,
      location: banner.location,
      background_color: banner.background_color,
      ab_testing_enabled: banner.ab_testing_enabled || false,
      ab_variants: banner.ab_variants || []
    });
  };

  const handleSaveBanner = async (bannerId) => {
    try {
      await axios.put(`${API_URL}/api/marketing/banners/${bannerId}`, bannerForm, { headers });
      toast.success('Banner updated!');
      setEditingBanner(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  const handleToggleBanner = async (bannerId) => {
    try {
      await axios.put(`${API_URL}/api/marketing/banners/${bannerId}/toggle`, {}, { headers });
      toast.success('Banner status updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle banner');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API_URL}/api/marketing/banners/${bannerId}`, { headers });
      toast.success('Banner deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  // Create new banner
  const handleCreateBanner = async () => {
    try {
      await axios.post(`${API_URL}/api/marketing/banners`, newBannerForm, { headers });
      toast.success('Banner created!');
      setShowCreateBanner(false);
      setNewBannerForm({
        name: '',
        title: '',
        text: '',
        cta_text: 'Learn More',
        cta_link: '/',
        location: 'homepage',
        background_color: '#003456',
        is_active: true
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create banner');
    }
  };

  // Create new coupon
  const handleCreateCoupon = async () => {
    try {
      const createData = {
        ...newCouponForm,
        minimum_order: newCouponForm.minimum_order ? parseFloat(newCouponForm.minimum_order) : null,
        max_uses: newCouponForm.max_uses ? parseInt(newCouponForm.max_uses) : null,
        valid_until: newCouponForm.valid_until || null
      };
      await axios.post(`${API_URL}/api/coupons`, createData, { headers });
      toast.success('Coupon created!');
      setShowCreateCoupon(false);
      setNewCouponForm({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        description: '',
        minimum_order: '',
        max_uses: '',
        valid_until: '',
        active: true
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create coupon');
    }
  };

  // Coupon handlers
  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon.id);
    setCouponForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      description: coupon.description || '',
      minimum_order: coupon.minimum_order || '',
      max_uses: coupon.max_uses || '',
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      active: coupon.active
    });
  };

  const handleSaveCoupon = async (couponId) => {
    try {
      const updateData = {
        ...couponForm,
        minimum_order: couponForm.minimum_order ? parseFloat(couponForm.minimum_order) : null,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        valid_until: couponForm.valid_until || null
      };
      await axios.put(`${API_URL}/api/coupons/${couponId}`, updateData, { headers });
      toast.success('Coupon updated!');
      setEditingCoupon(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API_URL}/api/coupons/${couponId}`, { headers });
      toast.success('Coupon deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  // Popup settings handler
  const handleSavePopupSettings = async () => {
    try {
      await axios.put(`${API_URL}/api/marketing/popup-settings`, popupSettings, { headers });
      toast.success('Popup settings saved!');
    } catch (error) {
      toast.error('Failed to save popup settings');
    }
  };

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-[#0a0203] mb-8">Marketing Analytics</h1>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { id: 'banners', label: 'Campaign Banners', icon: FileText },
          { id: 'coupons', label: 'Coupons & Discounts', icon: Gift },
          { id: 'visitors', label: 'Visitor Analytics', icon: BarChart3 },
          { id: 'subscribers', label: 'Subscribers', icon: Users },
          { id: 'popups', label: 'Popup Settings', icon: Bell }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab.id 
                ? 'bg-[#003456] text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`${tab.id}-tab-btn`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Campaign Banners Tab */}
      {activeTab === 'banners' && (
        <div className="space-y-6">
          {/* Create New Banner Button */}
          <div className="flex justify-end">
            <Button 
              onClick={() => setShowCreateBanner(true)}
              className="bg-[#003456] hover:bg-[#001d30] text-white"
              data-testid="create-banner-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Banner
            </Button>
          </div>

          {/* Create Banner Form */}
          {showCreateBanner && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#0a0203]">Create New Campaign Banner</h3>
                <button onClick={() => setShowCreateBanner(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Input 
                  placeholder="Banner Name (internal)" 
                  value={newBannerForm.name} 
                  onChange={(e) => setNewBannerForm({...newBannerForm, name: e.target.value})}
                />
                <Input 
                  placeholder="Display Title" 
                  value={newBannerForm.title} 
                  onChange={(e) => setNewBannerForm({...newBannerForm, title: e.target.value})}
                />
                <Input 
                  placeholder="Banner Text" 
                  value={newBannerForm.text} 
                  onChange={(e) => setNewBannerForm({...newBannerForm, text: e.target.value})}
                  className="md:col-span-2"
                />
                <Input 
                  placeholder="CTA Button Text" 
                  value={newBannerForm.cta_text} 
                  onChange={(e) => setNewBannerForm({...newBannerForm, cta_text: e.target.value})}
                />
                <Input 
                  placeholder="CTA Link URL" 
                  value={newBannerForm.cta_link} 
                  onChange={(e) => setNewBannerForm({...newBannerForm, cta_link: e.target.value})}
                />
                <select
                  value={newBannerForm.location}
                  onChange={(e) => setNewBannerForm({...newBannerForm, location: e.target.value})}
                  className="border rounded-lg px-3 py-2"
                >
                  <option value="homepage">Homepage</option>
                  <option value="topbar">Top Bar</option>
                  <option value="products">Products Page</option>
                  <option value="checkout">Checkout</option>
                  <option value="all">All Pages</option>
                </select>
                <Input 
                  type="color"
                  value={newBannerForm.background_color} 
                  onChange={(e) => setNewBannerForm({...newBannerForm, background_color: e.target.value})}
                  className="h-10"
                  title="Background Color"
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowCreateBanner(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateBanner}
                  className="bg-[#003456] hover:bg-[#001d30] text-white"
                  disabled={!newBannerForm.name || !newBannerForm.title}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Create Banner
                </Button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Banner</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Impressions</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Clicks</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">CTR</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {banners.map(banner => (
                  <React.Fragment key={banner.id}>
                    {editingBanner === banner.id ? (
                      <tr className="bg-blue-50">
                        <td colSpan={6} className="px-6 py-4">
                          <div className="bg-white rounded-lg p-4 space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <Input 
                                placeholder="Name" 
                                value={bannerForm.name} 
                                onChange={(e) => setBannerForm({...bannerForm, name: e.target.value})}
                              />
                              <Input 
                                placeholder="Title" 
                                value={bannerForm.title} 
                                onChange={(e) => setBannerForm({...bannerForm, title: e.target.value})}
                              />
                              <Input 
                                placeholder="Text" 
                                value={bannerForm.text} 
                                onChange={(e) => setBannerForm({...bannerForm, text: e.target.value})}
                              />
                              <Input 
                                placeholder="CTA Text" 
                                value={bannerForm.cta_text} 
                                onChange={(e) => setBannerForm({...bannerForm, cta_text: e.target.value})}
                              />
                              <Input 
                                placeholder="CTA Link" 
                                value={bannerForm.cta_link} 
                                onChange={(e) => setBannerForm({...bannerForm, cta_link: e.target.value})}
                              />
                              <select
                                value={bannerForm.location}
                                onChange={(e) => setBannerForm({...bannerForm, location: e.target.value})}
                                className="border rounded-lg px-3 py-2"
                              >
                                <option value="all">All Pages</option>
                                <option value="homepage">Homepage</option>
                                <option value="topbar">Top Bar</option>
                                <option value="products">Products Page</option>
                              </select>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" onClick={() => setEditingBanner(null)}>Cancel</Button>
                              <Button onClick={() => handleSaveBanner(banner.id)} className="bg-[#003456]">Save</Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#0a0203]">{banner.name}</p>
                            {banner.ab_testing_enabled && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                                <FlaskConical className="h-3 w-3" /> A/B
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{banner.title}</p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Eye className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{banner.impressions || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <MousePointer className="h-4 w-4 text-blue-500" />
                            <span className="font-medium">{banner.clicks || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`font-bold ${banner.ctr > 2 ? 'text-green-600' : banner.ctr > 0.5 ? 'text-amber-600' : 'text-gray-600'}`}>
                            {banner.ctr || 0}%
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button onClick={() => handleToggleBanner(banner.id)}>
                            {banner.is_active ? (
                              <ToggleRight className="h-6 w-6 text-green-500" />
                            ) : (
                              <ToggleLeft className="h-6 w-6 text-gray-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {banner.ab_testing_enabled && (
                              <button onClick={() => fetchBannerABStats(banner.id)} className="text-purple-500 hover:text-purple-700 p-1" title="A/B Stats">
                                <BarChart3 className="h-4 w-4" />
                              </button>
                            )}
                            <button onClick={() => handleEditBanner(banner)} className="text-blue-500 hover:text-blue-700 p-1">
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleDeleteBanner(banner.id)} className="text-red-500 hover:text-red-700 p-1">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {banners.length === 0 && (
              <p className="text-center py-8 text-gray-500">No campaign banners yet</p>
            )}
          </div>
        </div>
      )}

      {/* Coupons Tab */}
      {activeTab === 'coupons' && (
        <div className="space-y-6">
          {/* Create New Coupon Button */}
          <div className="flex justify-end">
            <Button 
              onClick={() => setShowCreateCoupon(true)}
              className="bg-[#003456] hover:bg-[#001d30] text-white"
              data-testid="create-coupon-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Coupon
            </Button>
          </div>

          {/* Create Coupon Form */}
          {showCreateCoupon && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#0a0203]">Create New Coupon</h3>
                <button onClick={() => setShowCreateCoupon(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Input 
                  placeholder="Coupon Code (e.g., SAVE20)" 
                  value={newCouponForm.code} 
                  onChange={(e) => setNewCouponForm({...newCouponForm, code: e.target.value.toUpperCase()})}
                />
                <select
                  value={newCouponForm.discount_type}
                  onChange={(e) => setNewCouponForm({...newCouponForm, discount_type: e.target.value})}
                  className="border rounded-lg px-3 py-2"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
                <Input 
                  type="number"
                  placeholder="Discount Value" 
                  value={newCouponForm.discount_value} 
                  onChange={(e) => setNewCouponForm({...newCouponForm, discount_value: e.target.value})}
                />
                <Input 
                  placeholder="Description (optional)" 
                  value={newCouponForm.description} 
                  onChange={(e) => setNewCouponForm({...newCouponForm, description: e.target.value})}
                  className="md:col-span-2"
                />
                <Input 
                  type="number"
                  placeholder="Minimum Order ($)" 
                  value={newCouponForm.minimum_order} 
                  onChange={(e) => setNewCouponForm({...newCouponForm, minimum_order: e.target.value})}
                />
                <Input 
                  type="number"
                  placeholder="Max Uses (blank = unlimited)" 
                  value={newCouponForm.max_uses} 
                  onChange={(e) => setNewCouponForm({...newCouponForm, max_uses: e.target.value})}
                />
                <Input 
                  type="date"
                  placeholder="Valid Until" 
                  value={newCouponForm.valid_until} 
                  onChange={(e) => setNewCouponForm({...newCouponForm, valid_until: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowCreateCoupon(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateCoupon}
                  className="bg-[#003456] hover:bg-[#001d30] text-white"
                  disabled={!newCouponForm.code || !newCouponForm.discount_value}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Create Coupon
                </Button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Code</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Discount</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Times Used</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Revenue Generated</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(coupon => (
                  <React.Fragment key={coupon.id}>
                    {editingCoupon === coupon.id ? (
                      <tr className="bg-blue-50">
                        <td colSpan={6} className="px-6 py-4">
                          <div className="bg-white rounded-lg p-4 space-y-4">
                            <div className="grid md:grid-cols-3 gap-4">
                              <Input 
                                placeholder="Code" 
                                value={couponForm.code} 
                                onChange={(e) => setCouponForm({...couponForm, code: e.target.value})}
                              />
                              <select
                                value={couponForm.discount_type}
                                onChange={(e) => setCouponForm({...couponForm, discount_type: e.target.value})}
                                className="border rounded-lg px-3 py-2"
                              >
                                <option value="percentage">Percentage (%)</option>
                                <option value="fixed">Fixed ($)</option>
                              </select>
                              <Input 
                                type="number"
                                placeholder="Discount Value" 
                                value={couponForm.discount_value} 
                                onChange={(e) => setCouponForm({...couponForm, discount_value: e.target.value})}
                              />
                              <Input 
                                placeholder="Description" 
                                value={couponForm.description} 
                                onChange={(e) => setCouponForm({...couponForm, description: e.target.value})}
                              />
                              <Input 
                                type="number"
                                placeholder="Minimum Order" 
                                value={couponForm.minimum_order} 
                                onChange={(e) => setCouponForm({...couponForm, minimum_order: e.target.value})}
                              />
                              <Input 
                                type="number"
                                placeholder="Max Uses" 
                                value={couponForm.max_uses} 
                                onChange={(e) => setCouponForm({...couponForm, max_uses: e.target.value})}
                              />
                              <Input 
                                type="date"
                                placeholder="Valid Until" 
                                value={couponForm.valid_until} 
                                onChange={(e) => setCouponForm({...couponForm, valid_until: e.target.value})}
                              />
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={couponForm.active}
                                  onChange={(e) => setCouponForm({...couponForm, active: e.target.checked})}
                                />
                                Active
                              </label>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" onClick={() => setEditingCoupon(null)}>Cancel</Button>
                              <Button onClick={() => handleSaveCoupon(coupon.id)} className="bg-[#003456]">Save</Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-mono font-bold text-[#003456]">{coupon.code}</p>
                          {coupon.description && <p className="text-sm text-gray-500">{coupon.description}</p>}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                            {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` :
                             `$${coupon.discount_value}`}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center font-medium">
                          {coupon.times_used || 0}
                          {coupon.max_uses && <span className="text-gray-400">/{coupon.max_uses}</span>}
                        </td>
                        <td className="px-6 py-4 text-center font-bold text-green-600" style={{ fontFamily: 'JetBrains Mono' }}>
                          ${(coupon.revenue_generated || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            coupon.active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {coupon.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => handleEditCoupon(coupon)} className="text-blue-500 hover:text-blue-700 p-1">
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleDeleteCoupon(coupon.id)} className="text-red-500 hover:text-red-700 p-1">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {coupons.length === 0 && (
              <p className="text-center py-8 text-gray-500">No coupons yet</p>
            )}
          </div>
        </div>
      )}

      {/* Visitor Analytics Tab */}
      {activeTab === 'visitors' && visitorStats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Eye className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                    {visitorStats.total_views?.toLocaleString()}
                  </p>
                  <p className="text-gray-500 text-sm">Total Page Views</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                    {visitorStats.unique_visitors?.toLocaleString()}
                  </p>
                  <p className="text-gray-500 text-sm">Unique Visitors</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                    {visitorStats.returning_visitors?.toLocaleString()}
                  </p>
                  <p className="text-gray-500 text-sm">Returning Visitors</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                    {visitorStats.new_visitors?.toLocaleString()}
                  </p>
                  <p className="text-gray-500 text-sm">New Visitors</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Popular Pages */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[#003456]" />
                Popular Pages
              </h3>
              <div className="space-y-3">
                {(visitorStats.popular_pages || []).map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700 truncate flex-1">{page.page}</span>
                    <span className="font-bold text-[#003456] ml-4">{page.views}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Geographic Breakdown */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5 text-[#003456]" />
                Visitors by Country
              </h3>
              <div className="space-y-3">
                {(visitorStats.geo_breakdown || []).map((geo, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      {geo.country}
                    </span>
                    <span className="font-bold text-[#003456]">{geo.views}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Daily Breakdown */}
          {visitorStats.daily_breakdown && visitorStats.daily_breakdown.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#0a0203] mb-4">Daily Visitor Log</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4 text-gray-600">Date</th>
                      <th className="text-center py-2 px-4 text-gray-600">Page Views</th>
                      <th className="text-center py-2 px-4 text-gray-600">Unique Visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitorStats.daily_breakdown.slice(-14).reverse().map((day, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="py-3 px-4">{day.date || day._id}</td>
                        <td className="py-3 px-4 text-center font-medium">{day.views}</td>
                        <td className="py-3 px-4 text-center font-medium text-green-600">{day.unique_visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Subscribers Tab */}
      {activeTab === 'subscribers' && (
        <div className="space-y-6">
          {/* Subscriber Segments */}
          {subscriberSegments && (
            <div className="grid md:grid-cols-3 gap-6">
              {/* By Country */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-[#003456]" />
                  By Country
                </h3>
                <div className="space-y-2">
                  {(subscriberSegments.by_country || []).slice(0, 8).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span>{item.country}</span>
                      <span className="font-bold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Source */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#003456]" />
                  By Source
                </h3>
                <div className="space-y-2">
                  {(subscriberSegments.by_source || []).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{item.source}</span>
                      <span className="font-bold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Month */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#003456]" />
                  By Month
                </h3>
                <div className="space-y-2">
                  {(subscriberSegments.by_month || []).slice(0, 6).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span>{item.month}</span>
                      <span className="font-bold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Subscriber List */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Location</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Source</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.slice(0, 50).map(sub => (
                  <tr key={sub.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">{sub.email}</td>
                    <td className="px-6 py-4">{sub.name || '-'}</td>
                    <td className="px-6 py-4">
                      {sub.geo ? (
                        <span className="flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          {sub.geo.city}, {sub.geo.country}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="capitalize text-sm">{sub.source || 'website'}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : 
                       sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Popup Settings Tab */}
      {activeTab === 'popups' && popupSettings && (
        <div className="space-y-6">
          {/* Timed Popup */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#003456]" />
              Timed Popup
            </h3>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={popupSettings.timed_popup?.enabled}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    timed_popup: {...popupSettings.timed_popup, enabled: e.target.checked}
                  })}
                />
                Enabled
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delay (seconds)</label>
                  <Input 
                    type="number"
                    value={popupSettings.timed_popup?.delay_seconds || 5}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      timed_popup: {...popupSettings.timed_popup, delay_seconds: parseInt(e.target.value)}
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Offer Code</label>
                  <Input 
                    value={popupSettings.timed_popup?.offer_code || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      timed_popup: {...popupSettings.timed_popup, offer_code: e.target.value}
                    })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
                  <Input 
                    value={popupSettings.timed_popup?.headline || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      timed_popup: {...popupSettings.timed_popup, headline: e.target.value}
                    })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body Text</label>
                  <textarea 
                    className="w-full border rounded-lg px-3 py-2"
                    rows={2}
                    value={popupSettings.timed_popup?.body || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      timed_popup: {...popupSettings.timed_popup, body: e.target.value}
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <Input 
                    value={popupSettings.timed_popup?.cta_text || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      timed_popup: {...popupSettings.timed_popup, cta_text: e.target.value}
                    })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Exit Intent Popup */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#003456]" />
              Exit Intent Popup
            </h3>
            <p className="text-sm text-gray-500 mb-4">Shows when visitor moves mouse towards browser close/back button</p>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={popupSettings.exit_intent_popup?.enabled}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    exit_intent_popup: {...popupSettings.exit_intent_popup, enabled: e.target.checked}
                  })}
                />
                Enabled
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Offer Code</label>
                  <Input 
                    value={popupSettings.exit_intent_popup?.offer_code || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      exit_intent_popup: {...popupSettings.exit_intent_popup, offer_code: e.target.value}
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <Input 
                    value={popupSettings.exit_intent_popup?.cta_text || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      exit_intent_popup: {...popupSettings.exit_intent_popup, cta_text: e.target.value}
                    })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
                  <Input 
                    value={popupSettings.exit_intent_popup?.headline || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      exit_intent_popup: {...popupSettings.exit_intent_popup, headline: e.target.value}
                    })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body Text</label>
                  <textarea 
                    className="w-full border rounded-lg px-3 py-2"
                    rows={2}
                    value={popupSettings.exit_intent_popup?.body || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      exit_intent_popup: {...popupSettings.exit_intent_popup, body: e.target.value}
                    })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Exit Popup */}
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-orange-400">
            <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-orange-500" />
              Checkout Exit Intent
            </h3>
            <p className="text-sm text-gray-500 mb-4">Shows when customer tries to leave the cart/checkout page or is inactive. Offers a last-chance discount to complete purchase.</p>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={popupSettings.checkout_exit_popup?.enabled ?? true}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), enabled: e.target.checked}
                  })}
                  className="rounded border-gray-300"
                />
                <span className="font-medium">Enabled</span>
              </label>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inactivity Trigger (seconds)</label>
                  <Input 
                    type="number"
                    min="10"
                    max="300"
                    value={popupSettings.checkout_exit_popup?.inactivity_seconds || 60}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), inactivity_seconds: parseInt(e.target.value)}
                    })}
                    placeholder="60"
                  />
                  <p className="text-xs text-gray-500 mt-1">Popup appears after this many seconds of no activity</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2"
                    value={popupSettings.checkout_exit_popup?.offer_type || 'percentage'}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), offer_type: e.target.value}
                    })}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Value</label>
                  <Input 
                    type="number"
                    min="0"
                    value={popupSettings.checkout_exit_popup?.offer_value || 5}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), offer_value: parseInt(e.target.value)}
                    })}
                    placeholder="5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Code</label>
                  <Input 
                    value={popupSettings.checkout_exit_popup?.offer_code || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), offer_code: e.target.value}
                    })}
                    placeholder="CHECKOUT5"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
                  <Input 
                    value={popupSettings.checkout_exit_popup?.headline || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), headline: e.target.value}
                    })}
                    placeholder="Wait! You're So Close!"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body Text</label>
                  <textarea 
                    className="w-full border rounded-lg px-3 py-2"
                    rows={2}
                    value={popupSettings.checkout_exit_popup?.body || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), body: e.target.value}
                    })}
                    placeholder="Complete your order now and get a special discount."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <Input 
                    value={popupSettings.checkout_exit_popup?.cta_text || ''}
                    onChange={(e) => setPopupSettings({
                      ...popupSettings,
                      checkout_exit_popup: {...(popupSettings.checkout_exit_popup || {}), cta_text: e.target.value}
                    })}
                    placeholder="Complete My Order"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Quote Popup */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <h3 className="font-bold">Quote Request Popup</h3>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={popupSettings.quote_popup?.enabled ?? true}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    quote_popup: { ...popupSettings.quote_popup, enabled: e.target.checked }
                  })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Enabled</span>
              </label>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Delay (seconds)</label>
                <Input
                  type="number"
                  value={popupSettings.quote_popup?.delay_seconds ?? 30}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    quote_popup: { ...popupSettings.quote_popup, delay_seconds: parseInt(e.target.value) || 30 }
                  })}
                  placeholder="30"
                />
                <p className="text-xs text-gray-500 mt-1">Time before popup appears on product pages</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Headline</label>
                <Input
                  value={popupSettings.quote_popup?.headline ?? 'Need Help Deciding?'}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    quote_popup: { ...popupSettings.quote_popup, headline: e.target.value }
                  })}
                  placeholder="Need Help Deciding?"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Subheadline</label>
                <Input
                  value={popupSettings.quote_popup?.subheadline ?? 'Get a personalized quote'}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    quote_popup: { ...popupSettings.quote_popup, subheadline: e.target.value }
                  })}
                  placeholder="Get a personalized quote"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Text</label>
                <Input
                  value={popupSettings.quote_popup?.cta_text ?? 'Request Quote'}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    quote_popup: { ...popupSettings.quote_popup, cta_text: e.target.value }
                  })}
                  placeholder="Request Quote"
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={popupSettings.quote_popup?.show_phone_field ?? true}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    quote_popup: { ...popupSettings.quote_popup, show_phone_field: e.target.checked }
                  })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Show Phone Field</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={popupSettings.quote_popup?.show_message_field ?? true}
                  onChange={(e) => setPopupSettings({
                    ...popupSettings,
                    quote_popup: { ...popupSettings.quote_popup, show_message_field: e.target.checked }
                  })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Show Message Field</span>
              </label>
            </div>
          </div>

          <Button onClick={handleSavePopupSettings} className="bg-[#003456]">
            <Save className="h-4 w-4 mr-2" />
            Save Popup Settings
          </Button>
        </div>
      )}

      {/* A/B Stats Modal */}
      {showABStats && abStats && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-600" /> 
                A/B Test Results - {showABStats.type === 'banner' ? 'Banner' : 'Coupon'}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowABStats(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4">
              {abStats.variants && abStats.variants.length > 0 ? (
                <>
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Variant Performance</h4>
                    {abStats.variants.map((v, idx) => (
                      <div key={v.id || idx} className={`p-3 rounded-lg mb-2 ${abStats.winner?.id === v.id ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{v.name}</span>
                          {abStats.winner?.id === v.id && (
                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">Winner</span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                          <div>
                            <span className="text-gray-500">Views:</span> {v.impressions || 0}
                          </div>
                          <div>
                            <span className="text-gray-500">Conv:</span> {v.conversions || 0}
                          </div>
                          <div>
                            <span className="text-gray-500">CVR:</span> {v.conversion_rate || 0}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {!abStats.winner && abStats.variants.length > 0 && (
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                      Need at least 100 impressions per variant to determine a winner.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-center text-gray-500 py-4">No A/B variants configured for this item.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingDashboard;
